import { FetchError } from './FetchError';
import { FETCH_TRANSFORMER_ERROR_CODE } from 'utils';

/**
 * PSI service-specific errors (PSI response.code > 0)
 *
 * @example
 * ```typescript
 * throw new PsiError('Email validation failed', 1001, { email: 'invalid' });
 * ```
 */
export class PsiError extends FetchError {
  override readonly type = 'PsiError';

  /**
   * @param message - PSI error message
   * @param psiCode - PSI response.code (1001, 2002, etc.)
   * @param details - Additional context
   * @param cause - Original error
   */
  constructor(
    message: string,
    public readonly psiCode: number,
    details?: Record<string, unknown>,
    cause?: Error,
  ) {
    super(message, FETCH_TRANSFORMER_ERROR_CODE.PSI_ERROR, undefined, { ...details, psiCode }, cause);
  }

  /**
   * Gets the PSI response data (field-level validation errors, etc.)
   * Provides easy access to error.data instead of error.details?.responseData
   *
   * @example
   * ```typescript
   * try {
   *   await createSupportTicket({ ... });
   * } catch (error) {
   *   if (error instanceof PsiError) {
   *     const fieldErrors = error.data;
   *     // fieldErrors = { fromEMail: [{ messageLocalized: '...' }], ... }
   *   }
   * }
   * ```
   */
  get data(): Record<string, unknown> | undefined {
    return this.details?.['responseData'] as Record<string, unknown> | undefined;
  }

  override toErrorObject() {
    return {
      code: this.code,
      psiCode: this.psiCode, // PSI errors include psiCode
      message: this.message,
      trace: this.stackTrace,
      statusCode: this.statusCode,
      data: this.data, // Include field-level validation errors for forms
    };
  }

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      psiCode: this.psiCode,
      data: this.data,
    };
  }
}
